import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useDomain } from "@/hooks/useDomain";
import { useToast } from "@/contexts/ToastContext";
import api from "@/lib/api";
import { registrationApi } from "@/lib/api/registration";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import AuthLayout from "@/components/layout/AuthLayout";
import { Loader2, Info } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const { login, loginWithCode } = useAuth();
  const { isMainPlatform, tenant, refreshTenant } = useTenant();
  const domainContext = useDomain();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const tenantSlugFromQuery = searchParams.get("tenantSlug");
  const codeFromUrl = searchParams.get("code");
  const returnUrlFromQuery = searchParams.get("returnUrl");

  useEffect(() => {
    if (!isMainPlatform && tenant) {
      // Tenant context is available
    }
  }, [isMainPlatform, tenant]);

  useEffect(() => {
    if (codeFromUrl) {
      window.history.replaceState({}, "", window.location.pathname);
      loginWithCode(codeFromUrl).then((ok) => {
        const destination = returnUrlFromQuery || "/dashboard";
        navigate(ok ? destination : "/login", { replace: true });
      });
    }
  }, [codeFromUrl, loginWithCode, navigate, returnUrlFromQuery]);

  const validateFields = () => {
    const errors: { email?: string; password?: string } = {};
    const trimmed = email.trim();
    if (!trimmed) {
      errors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!password) {
      errors.password = "Password is required.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorCode(null);

    if (!validateFields()) return;

    setIsLoading(true);

    try {
      const tenantSlug =
        tenantSlugFromQuery ||
        (!isMainPlatform ? domainContext.tenantSlug : undefined) ||
        undefined;
      const { redirectUrl, authCode } = await login(email, password, tenantSlug);

      // Defer refreshTenant so mobile browsers (especially iOS Safari) have time to process Set-Cookie.
      // Otherwise the next request can fire before cookies are available, causing 401 → redirect loop.
      await new Promise((r) => setTimeout(r, 150));
      await refreshTenant();

      const lastLocation = returnUrlFromQuery || sessionStorage.getItem("lastLocation");
      const publicRoutes = [
        "/login",
        "/",
        "/onboard",
        "/register",
        "/verify-email",
        "/reset-password",
      ];
      const shouldRestoreLocation =
        lastLocation && !publicRoutes.some((route) => lastLocation.startsWith(route));

      if (redirectUrl && redirectUrl.startsWith("http") && authCode) {
        const separator = redirectUrl.includes("?") ? "&" : "?";
        const returnParam = shouldRestoreLocation
          ? `&returnUrl=${encodeURIComponent(lastLocation!)}`
          : "";
        window.location.href = `${redirectUrl}${separator}code=${encodeURIComponent(authCode)}${returnParam}`;
      } else {
        const fallback = redirectUrl || "/dashboard";
        const targetUrl = shouldRestoreLocation ? lastLocation! : fallback;
        sessionStorage.removeItem("lastLocation");
        navigate(targetUrl, { replace: true });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Login failed";
      const code = err.response?.data?.code;
      setErrorCode(code || null);
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email.trim()) return;
    setResending(true);
    try {
      const tenantSlug =
        tenantSlugFromQuery ||
        (!isMainPlatform ? domainContext.tenantSlug : undefined) ||
        undefined;
      const result = await registrationApi.resendVerificationEmailByEmail(email.trim(), tenantSlug);
      toast.success(result.message);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to send. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const isTenantLogin = tenantSlugFromQuery || (!isMainPlatform && domainContext.tenantSlug);

  const pageTitle = isTenantLogin
    ? tenant
      ? `Sign in to ${tenant.name}`
      : "Sign in to your library"
    : "Sign in";

  const pageDescription = isTenantLogin
    ? "Enter your credentials to access your library account."
    : "Access the administration dashboard for your libraries.";

  const [forgotEmailError, setForgotEmailError] = useState("");
  const [forgotSending, setForgotSending] = useState(false);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotEmailError("");

    const trimmed = forgotPasswordEmail.trim();
    if (!trimmed) {
      setForgotEmailError("Email address is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setForgotEmailError("Please enter a valid email address.");
      return;
    }

    setForgotSending(true);
    try {
      await api.post("/auth/forgot-password", { email: trimmed });
      toast.success(
        "If an account exists with that email, you will receive password reset instructions."
      );
      setForgotPasswordEmail("");
      setTimeout(() => {
        setShowForgotPassword(false);
      }, 2000);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to send reset email. Please try again.");
    } finally {
      setForgotSending(false);
    }
  };

  const brandHeadline = isTenantLogin
    ? "Your library, organized."
    : "Library management, simplified.";

  const brandSubtext = isTenantLogin
    ? "Manage collections, serve patrons, and streamline operations — all in one place."
    : "A complete platform for managing catalogs, circulation, patrons, and more.";

  return (
    <AuthLayout headline={brandHeadline} subtext={brandSubtext}>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">
            {showForgotPassword ? "Reset your password" : pageTitle}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {showForgotPassword
              ? "Enter your email and we'll send you a reset link."
              : pageDescription}
          </p>
        </div>

        {/* Forgot password form */}
        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-5" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="forgot-email">
                Email address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => {
                  setForgotPasswordEmail(e.target.value);
                  if (forgotEmailError) setForgotEmailError("");
                }}
                placeholder="you@example.com"
                disabled={forgotSending}
                aria-invalid={!!forgotEmailError}
                aria-describedby={forgotEmailError ? "forgot-email-error" : undefined}
                className={
                  forgotEmailError
                    ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                    : ""
                }
              />
              {forgotEmailError && (
                <p id="forgot-email-error" className="text-sm text-red-600" role="alert">
                  {forgotEmailError}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={forgotSending}>
              {forgotSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send reset link"
              )}
            </Button>

            <p className="text-center text-sm text-slate-500">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail("");
                  setForgotEmailError("");
                }}
                className="text-primary font-medium hover:text-primary-600 transition-colors cursor-pointer"
              >
                Back to sign in
              </button>
            </p>
          </form>
        ) : (
          /* Login form */
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Resend verification prompt (shown after EMAIL_NOT_VERIFIED error) */}
            {errorCode === "EMAIL_NOT_VERIFIED" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleResendVerification}
                disabled={resending}
                className="w-full"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Resend verification email"
                )}
              </Button>
            )}

            {/* Email field */}
            <div className="space-y-1.5">
              <Label htmlFor="email">
                Email address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }));
                }}
                placeholder="you@example.com"
                disabled={isLoading}
                autoComplete="email"
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? "email-error" : undefined}
                className={
                  fieldErrors.email
                    ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                    : ""
                }
              />
              {fieldErrors.email && (
                <p id="email-error" className="text-sm text-red-600" role="alert">
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* Password field */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">
                  Password <span className="text-red-500">*</span>
                </Label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm font-medium text-primary hover:text-primary-600 transition-colors cursor-pointer"
                >
                  Forgot password?
                </button>
              </div>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password)
                    setFieldErrors((prev) => ({ ...prev, password: undefined }));
                }}
                placeholder="Enter your password"
                disabled={isLoading}
                autoComplete="current-password"
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? "password-error" : undefined}
                className={
                  fieldErrors.password
                    ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                    : ""
                }
              />
              {fieldErrors.password && (
                <p id="password-error" className="text-sm text-red-600" role="alert">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            {/* Submit */}
            <Button type="submit" disabled={isLoading} className="w-full" size="lg">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        )}

        {/* Footer links */}
        {!showForgotPassword && (
          <div className="space-y-3 text-center text-sm text-slate-500">
            {isTenantLogin && (
              <p>
                <Link
                  to="/opac"
                  className="text-primary font-medium hover:text-primary-600 transition-colors"
                >
                  Explore catalog as guest
                </Link>
              </p>
            )}
            <p>
              Don&apos;t have an account?{" "}
              <Link
                to={isTenantLogin ? "/register" : "/onboard"}
                className="text-primary font-medium hover:text-primary-600 transition-colors"
              >
                Sign up
              </Link>
            </p>
          </div>
        )}

        {/* Dev mode info */}
        {!showForgotPassword && isMainPlatform && !tenantSlugFromQuery && import.meta.env.DEV && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="text-sm text-primary">
                <p>
                  Staff and library users: Sign in at your library's portal (e.g.,
                  yourlibrary.philolms.com)
                </p>
                <p className="mt-1.5 text-xs text-primary/70">
                  Dev mode: add{" "}
                  <code className="bg-primary/10 px-1 py-0.5 rounded text-primary">
                    ?tenantSlug=your-library-slug
                  </code>{" "}
                  to the URL
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
